import { IAccessToken, getAccessToken, getAdminAccessToken, getRefreshToken, sendCodeForAdminLogin } from '../services'
import { MsalAuthProvider, LoginType, IAccountInfo, AuthenticationState } from 'react-aad-msal';
import { removeData, retrieveData, storeData, handleError } from '../components/shared/Functions';

/* Must include redirectUri config.  Without, it is possible to have an
   edge-case issue where the user proceeds with the login flow from a route
   other than root and cause a sporadic login error */
const env = process.env.REACT_APP_ENV_NAME?.toLowerCase();

const msalConfig = {
    auth: {
        authority: 'https://login.microsoftonline.com/99895822-a26f-4e70-a993-354f47cf2427/',
        clientId: env === "production" ? 'fd0a1571-4793-4395-a603-ab03140a32aa' : '394af54d-2ad8-4b23-a037-757cc70fe648',
        postLogoutRedirectUri: window.location.origin,
        redirectUri: window.location.origin,
        validateAuthority: true,
        navigateToLoginRequestUrl: false
    }
};

export function isExpired(time: Date) {
    const millisRemaining = time.getTime() - Date.now();
    return millisRemaining < 305000 ? true : false;
}

const loginRequest = {
    scopes: ["openid", "profile"]
};

const msalOptions = {
    loginType: LoginType.Popup,
    tokenRefreshUri: window.location.origin + "/auth.html"
};

export interface ISecurity {
    authenticationState: string;
    accountInfo?: IAccountInfo;
    getAccessToken?: (() => Promise<string>);
    login: () => void;
    logout: () => void;
}

export class NexusAuthProvider extends MsalAuthProvider {
    protected _accessToken: IAccessToken | null;

    constructor(config, parameters, options) {
        super(config, parameters, options);
        let tokenString = retrieveData("token");
        let token = tokenString ? {
            ...tokenString,
            tokenExpires: new Date(),
            refreshTokenExpires: new Date(tokenString.refreshTokenExpires)
        } : null;
        if(token && !isExpired(token.refreshTokenExpires)){
            this._accessToken = token;
        } else {
            this._accessToken = null;
        }
    }

    public getNexusToken = async (): Promise<string> => {
        const _nexusTokenRequest = (resolve, reject) => {
            if(this.authenticationState !== AuthenticationState.Authenticated && !this._accessToken)
                resolve(null);
            else if(!this._accessToken) {
                this.getIdToken()
                    .then(idToken => {
                        getAccessToken(idToken.idToken.rawIdToken)
                            .then(token => {
                                this._accessToken = token;
                                resolve(token.token);
                            })
                            .catch(err => resolve(null));
                    })
                    .catch(err => resolve(null));
            } else if (isExpired(this._accessToken.refreshTokenExpires)) {
                this.logoutCredentials();
            } else if(isExpired(this._accessToken.tokenExpires)) {
                getRefreshToken(this._accessToken.refreshToken)
                    .then(token => {
                        this._accessToken = token;
                        resolve(token.token);
                    })
                    .catch(err => handleError(err));
            } else {
                resolve(this._accessToken.token);
            }
        }
        return new Promise<string>(_nexusTokenRequest);
    }

    public getNexusTokenFull = async (): Promise<IAccessToken> => {
        const _nexusTokenRequest = (resolve, reject) => {
            if(this.authenticationState !== AuthenticationState.Authenticated && !this._accessToken)
                return;
            if(!this._accessToken) {
                this.getIdToken()
                    .then(idToken => {
                        getAccessToken(idToken.idToken.rawIdToken)
                            .then(token => {
                                this._accessToken = token;
                                resolve(token);
                            })
                            .catch(err => handleError(err));
                    })
                    .catch(err => handleError(err));
            } else if (isExpired(this._accessToken.refreshTokenExpires)) {
                this.logoutCredentials();
            } else if(isExpired(this._accessToken.tokenExpires)) {
                getRefreshToken(this._accessToken.refreshToken)
                    .then(token => {
                        this._accessToken = token;
                        resolve(token);
                    })
                    .catch(err => handleError(err));
            } else {
                resolve(this._accessToken);
            }
        }
        return new Promise<IAccessToken>(_nexusTokenRequest);
    }

    public getPermissions = (token : string): any => {
        if(this._accessToken !== null){
            return JSON.parse(atob(this._accessToken.token.split('.')[1])).Permissions;
        }
    }

    public getUser = (): any => {
        if(this._accessToken !== null){
            return JSON.parse(atob(this._accessToken.token.split('.')[1]));
        }
        return null;
    }

    public loginCredentials = (user) => {
        const _loginAdmin = (resolve, reject) => {
            sendCodeForAdminLogin(user)
                .then(response => {
                    resolve(response);
                })
                .catch(err => reject(err));
        }
        return new Promise<string>(_loginAdmin);
    }

    public loginCode(user, code) {
        const _loginAdmin = (resolve, reject) => {
            getAdminAccessToken(user, code)
                .then(token => {
                    storeData("token", token);
                    this._accessToken = token;
                    resolve(true);
                })
                .catch(err => reject(err));
        }
        return new Promise<string>(_loginAdmin);
    }

    public logoutCredentials = () => {
        if(this.authenticationState === AuthenticationState.Authenticated) {
            this.logout();
        } else {
            removeData("token");
            this._accessToken = null;
        }
    }

    public checkLogin = () => {
        return this._accessToken !== null;
    }
}

export const authProvider = new NexusAuthProvider(msalConfig, loginRequest, msalOptions);